import { lazy, Suspense } from "react";

import { Center, Link, useBreakpoint } from "@seneca/senekit";

import { currentStudentAssignmentsAreFetching } from "seneca-common/features/assignments/features/student-assignments/state";
import { useDispatch } from "seneca-common/utils/hooks/dispatch";
import { useSelector } from "seneca-common/utils/hooks/selectors";
import { useMomentUpdatingAtInterval } from "seneca-common/utils/hooks/useMoment";

import {
  SidebarContentError,
  SidebarContentHeader,
  SidebarContentWrapper
} from "features/classroom/features/sidebar/features/common";
import { closeSidebarIfOpen } from "features/classroom/state";
import ErrorHandler from "features/common/components/ErrorHandler";
import TextPlaceholder from "features/common/components/TextPlaceholder";
import { ROUTES } from "routing/routes";

import sidebarStyles from "../../Sidebar.module.scss";
import styles from "./Assignments.module.scss";
import AssignmentsList from "./components/AssignmentsList";
import AssignmentsItemsCounter from "./components/AssignmentsSectionsCounter";
import useGetAssignmentIds from "./hooks/useGetAssignmentIds";

const NoAssignmentsAnimation = lazy(
  () =>
    import(
      /* webpackChunkName: "no-assignments-animation" */ "features/teachers/common/components/NoAssignmentsAnimation/NoAssignmentsAnimation"
    )
);

type Props = {
  courseId: string;
  activeSectionId: string;
  isSelected: boolean;
};

function Assignments({ courseId, activeSectionId, isSelected }: Props) {
  const now = useMomentUpdatingAtInterval(600);
  const { isMobileOnly } = useBreakpoint();
  const dispatch = useDispatch();

  const loading = useSelector(currentStudentAssignmentsAreFetching);

  const { assignmentIds } = useGetAssignmentIds();

  const numberOfAssignments = assignmentIds.length;

  return (
    <div className={sidebarStyles.carouselWrapper}>
      <SidebarContentWrapper
        className={styles.wrapper}
        dataTest="AssignmentsWrapper"
        hasTabBar={false}
      >
        <SidebarContentHeader className={styles.header}>
          {{
            title: <>Assignments</>,
            description:
              numberOfAssignments === 0 && isSelected ? (
                <TextPlaceholder loading={loading} placeholderWidth="100%">
                  No assignments to do. You can find your past assignments for
                  this course on the{" "}
                  <Link
                    to={ROUTES.CLASSROOM.COURSE.ASSIGNMENTS.DONE.buildPath({
                      courseId
                    })}
                    size="medium"
                    onClick={() =>
                      isMobileOnly && dispatch(closeSidebarIfOpen())
                    }
                  >
                    assignments tab
                  </Link>
                  .
                </TextPlaceholder>
              ) : null,
            counter: <AssignmentsItemsCounter />
          }}
        </SidebarContentHeader>
        {isSelected &&
          (numberOfAssignments > 0 ? (
            <AssignmentsList
              activeSectionId={activeSectionId}
              assignmentIds={assignmentIds}
              now={now}
            />
          ) : (
            <Center>
              <Suspense fallback={null}>
                <NoAssignmentsAnimation width={120} />
              </Suspense>
            </Center>
          ))}
      </SidebarContentWrapper>
    </div>
  );
}

export default ErrorHandler(Assignments, SidebarContentError);
